import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SEMI_FRESH, STATIC } from '../../lib/queryPresets';
import {
  createTimeEntry,
  deleteTimeEntry,
  listBillingCodes,
  listTimeEntries,
  updateTimeEntry,
  type CreateTimeEntryInput,
  type TimeEntry,
  type TimeEntryFilters,
  type UpdateTimeEntryInput,
} from '../../services/timeTracking/timeTrackingService';

export const timeEntriesKeys = {
  all: ['time_entries'] as const,
  list: (filters: TimeEntryFilters) => [...timeEntriesKeys.all, 'list', filters] as const,
  billingCodes: () => [...timeEntriesKeys.all, 'billing_codes'] as const,
};

export function useTimeEntries(filters: TimeEntryFilters = {}) {
  return useQuery({
    ...SEMI_FRESH,
    queryKey: timeEntriesKeys.list(filters),
    queryFn: () => listTimeEntries(filters),
  });
}

export function useBillingCodes() {
  return useQuery({
    ...STATIC,
    queryKey: timeEntriesKeys.billingCodes(),
    queryFn: listBillingCodes,
  });
}

export function useCreateTimeEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTimeEntryInput) => createTimeEntry(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: timeEntriesKeys.all });
    },
  });
}

export function useUpdateTimeEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateTimeEntryInput }) =>
      updateTimeEntry(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: timeEntriesKeys.all });
    },
  });
}

export function useDeleteTimeEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteTimeEntry(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: timeEntriesKeys.all });
    },
  });
}

export type { TimeEntry };
